import React, { useEffect, useState, useCallback, useRef } from "react";
import "./PopupSlider.css";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;

const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

async function fetchPopups() {
  const res = await fetch(`${BASE}/api/cms/popups/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Popups unavailable");
  const json = await res.json();
  // DRF paginated → take results; if unpaginated, use as-is
  return Array.isArray(json) ? json : json.results || [];
}

export default function PopupSlider() {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const touchStartX = useRef(null);

  // ── fetch on mount ──
  useEffect(() => {
    let cancelled = false;
    fetchPopups()
      .then((data) => {
        if (cancelled) return;
        const active = data
          .filter((p) => p.is_active)
          .sort((a, b) => a.display_order - b.display_order);
        if (active.length) {
          setSlides(active);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ── lock body scroll while open ──
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // ── keyboard: ESC, ←, → ──
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  // ── swipe ──
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) next();
    else prev();
  };

  if (!open || slides.length === 0) return null;

  const slide = slides[index];

  return (
    <div
      className="popup-overlay"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Announcement"
    >
      <div
        className="popup-shell"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* close */}
        <button
          className="popup-close"
          onClick={close}
          aria-label="Close"
          type="button"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {/* prev arrow */}
        {slides.length > 1 && (
          <button
            className="popup-arrow left"
            onClick={prev}
            aria-label="Previous"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                 stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
                 strokeLinejoin="round">
              <polyline points="15 6 9 12 15 18" />
            </svg>
          </button>
        )}

        {/* image */}
        <div className="popup-image-wrap">
          <img
            key={slide.id}
            src={slide.image}
            alt={slide.alt_text || slide.title}
            className="popup-image"
            draggable="false"
          />
        </div>

        {/* next arrow */}
        {slides.length > 1 && (
          <button
            className="popup-arrow right"
            onClick={next}
            aria-label="Next"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                 stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"
                 strokeLinejoin="round">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        )}

        {/* dots */}
        {slides.length > 1 && (
          <div className="popup-dots" onClick={(e) => e.stopPropagation()}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`popup-dot ${i === index ? "active" : ""}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}